import { Doc } from '@dadohr/shared/domainTypes/document';
import { EMPTY_OBJ } from '@dadohr/shared/domainTypes/emptyConstants';
import { isCustomFieldKey } from '@dadohr/shared/domainTypes/org';
import { IUser, UserChanged } from '@dadohr/shared/domainTypes/user';
import styled from '@emotion/styled';
import { keyBy, mapValues } from 'lodash';
import React, { useMemo } from 'react';
import { useSort } from '../../hooks/useSort';
import {
  ItemSorter,
  ItemSorters,
  ROW_HEIGHTS,
  RowsRenderer
} from '../GroupableList';
import { IColumn } from '../Table/VirtualizedSortable';
import { ColumnName, UserTableColumn, UserTableItemSorters } from './columns';

type D = UserChanged;
type O = { dateFormat: string; additionalUsers: { [key: string]: Doc<IUser> }, highlightedFields?: Record<string, Set<string>>; };

type Column = IColumn<D, ColumnName, O>;

const Before = styled('div')((p) => ({
  fontSize: p.theme.custom.fontSize.s,
  color: p.theme.custom.colors.gray.w400,
  textDecoration: 'line-through'
}));

const After = styled('div')((p) => ({
  color: p.theme.custom.colors.success.main
}));

const DiffColumn = ({
  d,
  o,
  cell,
  hasChanges
}: {
  d: D;
  o: O;
  cell: (user: Doc<IUser>, other: O) => React.ReactNode;
  hasChanges: boolean;
}) => {
  if (!hasChanges) {
    return <div>{cell(d.aft, o)}</div>;
  }
  return (
    <div>
      <Before>{cell(d.bef, o)}</Before>
      <After>{cell(d.aft, o)}</After>
    </div>
  );
};

const hasChanged = (d: UserChanged, key: ColumnName): boolean => {
  switch (key) {
    case 'name':
      return !!(d.changes.preferredName || d.changes.lastName);
    case 'firstName':
      return !!d.changes.firstName;
    case 'lastName':
      return !!d.changes.lastName;
    case 'preferredName':
      return !!d.changes.preferredName;
    case 'email':
      return !!d.changes.email;
    case 'personalEmail':
      return !!d.changes.personalEmail;
    case 'role':
      return !!d.changes.role;
    case 'location':
      return !!d.changes.location;
    case 'department':
      return !!d.changes.department;
    case 'hireDate':
      return !!d.changes.hireDate;
    case 'terminationDate':
      return !!d.changes.terminationDate;
    case 'dateOfBirth':
      return !!d.changes.dateOfBirth;
    case 'managerId':
      return !!d.changes.managerId;
    case 'team':
      return !!d.changes.team;
    case 'alternateId':
      return !!d.changes.alternateId;
    case 'actions':
      return false;
    default:
      if (isCustomFieldKey(key)) {
        return !!d.changes[key];
      }
      console.log('unhandled change key', key);
      return false;
  }
};

const toDiffColumn = (column: UserTableColumn): Column => ({
  ...column,
  cell: (d, o) => (
    <DiffColumn
      d={d}
      o={o}
      cell={column.cell}
      hasChanges={hasChanged(d, column.key)}
    />
  )
});

const toDiffSorter = (v: ItemSorter<Doc<IUser>, O>): ItemSorter<D, O> => ({
  ...v,
  items: {
    ...v.items,
    sort:
      typeof v.items.sort === 'function'
        ? (d: D, _o: O) => {
            if (typeof v.items.sort !== 'function') {
              // can never happen, but help typescript out here
              return '';
            }
            return v.items.sort(d.aft, _o);
          }
        : v.items.sort.map((fn) => (_d: D, _o: O) => fn(_d.aft, _o))
  }
});

const ROW_TO_KEY = (d: D) => d.aft.id;

export const UserDiffTable = ({
  ds,
  columns,
  sorters: _sorters,
  dateFormat,
  rowToHref,
  onRowClick,
  variant = 'contained',
  additionalUsers
}: {
  ds: UserChanged[];
  columns: UserTableColumn[];
  sorters: UserTableItemSorters;
  dateFormat: string;
  rowToHref?: (d: UserChanged) => string;
  onRowClick?: (d: UserChanged) => void;
  variant?: 'contained' | 'plain';

  // See explanation in normal UserTable
  additionalUsers?: Doc<IUser>[];
}) => {
  const sorters: ItemSorters<D, O> = useMemo(
    () => mapValues(_sorters, toDiffSorter),
    [_sorters]
  );
  const { sortBy, direction, setSort } = useSort<ItemSorter<D, O>>(
    sorters.name,
    sorters.name.items.dir
  );
  const diffColumns = useMemo(() => columns.map(toDiffColumn), [columns]);

  const otherProps: O = useMemo(() => {
    return {
      dateFormat,
      additionalUsers: additionalUsers
        ? keyBy(additionalUsers, (a) => a.id)
        : EMPTY_OBJ
    };
  }, [additionalUsers, dateFormat]);

  return (
    <RowsRenderer
      variant={variant}
      columns={diffColumns}
      rows={ds}
      rowToKey={ROW_TO_KEY}
      rowToHref={rowToHref}
      onRowClick={onRowClick}
      sorter={sortBy}
      sortDirection={direction}
      renderHead={true}
      headProps={{ sticky: true }}
      onHeadClick={(c, dir) => {
        const sorter = sorters[c.key];
        if (!sorter) {
          return;
        }
        setSort(sorter, dir || sorter.items.dir);
      }}
      chunkSize={30}
      rootMargin="300px"
      rowHeight={ROW_HEIGHTS.dense}
      otherProps={otherProps}
    />
  );
};
